import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import ProviderManager from '../../../provider/providerManager';
import {TAB_ENUM} from 'app/Components/Page/SelfCarePage/Constant';
import {Onboarding} from 'app/Components/Page/SelfCarePage/SelfCareAsyncThunk';
import {showAppLoader, hideAppLoader} from 'app/Components/Shared/Loader/LoaderSlice';

async function ensureOnboardingLoaded(thunkAPI) {
  const cellular = thunkAPI.getState().selfcarePage?.subscribersByProduct?.[TAB_ENUM.Cellular];
  if (!cellular?.length || cellular.some((item) => !item.consumerId)) {
    await thunkAPI.dispatch(Onboarding());
  }
}

export const checkServiceAndGetSubscribers = createAsyncThunk('cellularDataOnOff/CheckServiceAndGetSubscribers', async ({blockId, isPaywalled}, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    await ensureOnboardingLoaded(thunkAPI);
    const response = await ProviderManager.getProviderData('CheckServiceAndGetSubscribers', [blockId]);
    const subscribers = response.Body.subscribers;
    return {subscribers, isPaywalled, error: false};
  } catch (error) {
    return {error: true};
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const init = createAsyncThunk('cellularDataOnOff/init', async (subscriberNumber, thunkAPI) => {
  try {
    return await ProviderManager.getProviderData('CellularDataOnOffInit', [subscriberNumber]);
  } catch (error) {
    return {error: true};
  }
});

export const StepsEnum = {
  SELECT_SUBSCRIBER: 'SELECT_SUBSCRIBER',
  TOGGLE_STEP: 'TOGGLE_STEP',
  ERROR: 'ERROR',
  BUY_SERVICE: 'BUY_SERVICE'
};

export const toggleData = createAsyncThunk('cellularDataOnOff/toggleData', async (socId, thunkAPI) => {
  try {
    const state = thunkAPI.getState().cellularDataOnOffSlice;
    return await ProviderManager.getProviderData('ToggleData', [state.selectedSubscriber, socId]);
  } catch (error) {
    return {error: true};
  }
});

export const PopUp = {
  CALL_BACK: 'CALL_BACK',
};

export const PROCESS_STATUS_ENUM = {
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error'
};

export const ACTION_ENUM = {
  BLOCK: 'block',
  UNBLOCK: 'unblock'
};

const CellularDataOnOffSlice = createSlice({
  name: 'cellularDataOnOffSlice',
  initialState: {
    isLoaded: null,
    step: null,
    popUp: null,
    subscriberList: null,
    selectedSubscriber: null,
    error: null,
    isCollapseOpen: false,
    isBlocked: false,
    quantity: null,
    used: null,
    percentageUsed: null,
    quantityUnitOfMeasure: null,
    usedUnitOfMeasure: null,
    cycleRemainingDays: null,
    processStatus: null,
    action: null,
    isUnlimited: false
  },
  reducers: {
    setIsLoaded: (state, action) => {
      state.isLoaded = action.payload;
    },
    setStep: (state, action) => {
      state.step = action.payload;
    },
    setPopUp: (state, action) => {
      state.popUp = action.payload;
    },
    setSelectedSubscriber: (state, action) => {
      state.selectedSubscriber = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    toggleCollapse: (state) => {
      state.isCollapseOpen = !state.isCollapseOpen;
    },
    saveToHistory: (state, action) => {
      return state;
    },
  },
  extraReducers: {
    [checkServiceAndGetSubscribers.fulfilled]: (state, action) => {
      if (action.payload.error) {
        state.step = StepsEnum.ERROR;
        return;
      }

      state.subscriberList = action.payload.subscribers.map((subscriber) => ({
        value: subscriber,
        key: subscriber.substring(0, 3) + '-' + subscriber.substring(3),
      }));

      if (action.payload.subscribers.length === 1) {
        state.selectedSubscriber = action.payload.subscribers[0];
        state.step = StepsEnum.TOGGLE_STEP;
      } else {
        state.step = StepsEnum.SELECT_SUBSCRIBER;
        setTimeout(() => {
          state.saveToHistory("subscriberSelect");
        }, 2000);
      }
    },
    [init.fulfilled]: (state, action) => {
      if (action.payload.error) {
        state.step = StepsEnum.ERROR;
        return;
      }
      const initResult = action.payload.Body;

      state.quantity = initResult.dataUsage?.quantity;
      state.used = initResult.dataUsage?.used;
      state.percentageUsed = initResult.dataUsage?.percentageUsed;
      state.quantityUnitOfMeasure = initResult.dataUsage?.quantityUnitOfMeasure;
      state.usedUnitOfMeasure = initResult.dataUsage?.usedUnitOfMeasure;
      state.isBlocked = initResult.isBlocked;
      state.cycleRemainingDays = initResult.cycleRemainingDays;
      state.statusBarWidth = initResult.dataUsage?.statusBarWidth;
      state.isUnlimited = initResult.isUnlimited;
    },
    [toggleData.pending]: (state, action) => {
      state.action = state.isBlocked ? ACTION_ENUM.UNBLOCK : ACTION_ENUM.BLOCK;
      state.popUp = PopUp.CALL_BACK;
      state.processStatus = PROCESS_STATUS_ENUM.PROCESSING;
    },
    [toggleData.fulfilled]: (state, action) => {
      state.popUp = PopUp.CALL_BACK;
      state.processStatus = action.payload.error ? PROCESS_STATUS_ENUM.ERROR : PROCESS_STATUS_ENUM.SUCCESS;
      if (!action.payload.error) {
        state.isBlocked = !state.isBlocked;
      }
    },
  }
});

export const {
  setIsLoaded,
  setStep,
  setPopUp,
  setSelectedSubscriber,
  setError,
  toggleCollapse,
  saveToHistory
} = CellularDataOnOffSlice.actions;

export default CellularDataOnOffSlice.reducer;
